import React, { useState, useEffect } from "react";
import { Grid, Card, CardContent, Typography, Box, Chip } from "@mui/material";
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Link as LinkIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as MoneyIcon,
  LocalShipping as ShippingIcon,
} from "@mui/icons-material";

const OverviewTab = () => (
  <Card>
    <CardContent>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Multi-Marketplace Repricer
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Advanced repricing solution with automated price optimization for Otto
          and Kaufland marketplaces. Featuring web scraping, dynamic margin
          rules, and intelligent min/max price management.
        </Typography>
      </Box>

      <Typography
        variant="h6"
        gutterBottom
        sx={{ mt: 4, mb: 2, fontWeight: 600 }}
      >
        Core Features
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <Card
            variant="outlined"
            sx={{
              height: "100%",
              borderLeft: 3,
              borderLeftColor: "primary.main",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TrendingUpIcon color="primary" sx={{ fontSize: 32, mr: 1 }} />
                <Typography variant="h6">Otto Web Scraping</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Real-time competitor price monitoring with automated scraping
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label="Min/Max Pricing"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label="Auto-Repricing"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card
            variant="outlined"
            sx={{
              height: "100%",
              borderLeft: 3,
              borderLeftColor: "success.main",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ShoppingCartIcon
                  color="success"
                  sx={{ fontSize: 32, mr: 1 }}
                />
                <Typography variant="h6">Kaufland Integration</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Full API integration with intelligent price boundaries
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label="Min/Max Pricing"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label="HMAC Auth"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card
            variant="outlined"
            sx={{
              height: "100%",
              borderLeft: 3,
              borderLeftColor: "warning.main",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <MoneyIcon color="warning" sx={{ fontSize: 32, mr: 1 }} />
                <Typography variant="h6">Dynamic Margins</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Automated margin calculation based on buy price tiers
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label="Min Margin"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label="Max Margin"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card
            variant="outlined"
            sx={{ height: "100%", borderLeft: 3, borderLeftColor: "info.main" }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ShippingIcon color="info" sx={{ fontSize: 32, mr: 1 }} />
                <Typography variant="h6">Package Mappings</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Weight-based package type and cost calculation
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label="Auto-Mapping"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label="Cost Calc"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card
            variant="outlined"
            sx={{
              height: "100%",
              borderLeft: 3,
              borderLeftColor: "error.main",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SettingsIcon color="error" sx={{ fontSize: 32, mr: 1 }} />
                <Typography variant="h6">Provision Rules</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Category-based provision percentage configuration
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label="Per Category"
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label="Auto-Apply"
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card
            variant="outlined"
            sx={{
              height: "100%",
              borderLeft: 3,
              borderLeftColor: "secondary.main",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <RefreshIcon color="secondary" sx={{ fontSize: 32, mr: 1 }} />
                <Typography variant="h6">Automated Workflows</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Scheduled repricing with XLSX upload/download support
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label="Cron Jobs"
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label="Bulk Updates"
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography
        variant="h6"
        gutterBottom
        sx={{ mt: 4, mb: 2, fontWeight: 600 }}
      >
        Marketplace Capabilities
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Otto Marketplace
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                Complete repricing solution with competitor monitoring
              </Typography>
              <Box component="ul" sx={{ pl: 2, "& li": { mb: 1 } }}>
                <li>Real-time web scraping for competitor prices</li>
                <li>Min/Max price boundaries per SKU</li>
                <li>Automated price updates via Otto API</li>
                <li>Buy Box tracking and optimization</li>
                <li>Historical price tracking</li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            variant="outlined"
            sx={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Kaufland Marketplace
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                API-driven price management with smart boundaries
              </Typography>
              <Box component="ul" sx={{ pl: 2, "& li": { mb: 1 } }}>
                <li>HMAC-SHA256 authenticated API calls</li>
                <li>Min/Max price boundaries per SKU</li>
                <li>Rate limiting (111 req/sec) with retry logic</li>
                <li>Multi-storefront support (DE, AT, CZ, SK, PL)</li>
                <li>Automatic article synchronization</li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

export default OverviewTab;
